import { useCallback, useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { toast } from 'sonner';
import { StatusPill } from '@/components/ui/StatusPill';
import { errorMessage, tokenApi, type TokenEvent } from './api';
import { EVENT_LABELS, formatDateTime, providerLabel } from './format';
import {
  EmptyRow,
  Pager,
  ProviderCell,
  TableShell,
  Td,
  TenantCell,
  TenantSelect,
  Th,
} from './shared';
import { controlClass, useDebounced, useTenantOptions } from './hooks';

const PAGE_SIZE = 25;

const RANGES: { label: string; hours: number | null }[] = [
  { label: 'Last 24 hours', hours: 24 },
  { label: 'Last 7 days', hours: 24 * 7 },
  { label: 'Last 30 days', hours: 24 * 30 },
  { label: 'All retained', hours: null },
];

export const ActivityTab = () => {
  const tenants = useTenantOptions();
  const [providers, setProviders] = useState<string[]>([]);
  const [tenant, setTenant] = useState('');
  const [user, setUser] = useState('');
  const debouncedUser = useDebounced(user);
  const [eventType, setEventType] = useState('');
  const [outcome, setOutcome] = useState('');
  const [provider, setProvider] = useState('');
  const [rangeHours, setRangeHours] = useState<number | null>(24 * 7);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<{ items: TokenEvent[]; total: number }>({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    tokenApi
      .overview(1)
      .then((o) => setProviders(o.providers.map((p) => p.provider)))
      .catch(() => setProviders([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await tokenApi.events({
        tenant,
        user: debouncedUser,
        event_type: eventType,
        outcome,
        provider,
        since: rangeHours ? new Date(Date.now() - rangeHours * 3600_000).toISOString() : undefined,
        page,
        page_size: PAGE_SIZE,
      });
      setData({ items: res.items, total: res.total });
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to load token activity'));
    } finally {
      setLoading(false);
    }
  }, [tenant, debouncedUser, eventType, outcome, provider, rangeHours, page]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => setPage(1), [tenant, debouncedUser, eventType, outcome, provider, rangeHours]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={user}
            onChange={(e) => setUser(e.target.value)}
            placeholder="Search by user email"
            aria-label="Search by user email"
            className={`${controlClass} pl-9 w-64`}
          />
        </div>
        <TenantSelect value={tenant} onChange={setTenant} tenants={tenants} />
        <select aria-label="Event" value={eventType} onChange={(e) => setEventType(e.target.value)} className={controlClass}>
          <option value="">All events</option>
          {Object.entries(EVENT_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select aria-label="Outcome" value={outcome} onChange={(e) => setOutcome(e.target.value)} className={controlClass}>
          <option value="">Any outcome</option>
          <option value="success">Success</option>
          <option value="failure">Failure</option>
        </select>
        <select aria-label="Identity provider" value={provider} onChange={(e) => setProvider(e.target.value)} className={controlClass}>
          <option value="">All providers</option>
          {providers.map((p) => (
            <option key={p} value={p}>
              {providerLabel(p)}
            </option>
          ))}
        </select>
        <select
          aria-label="Time range"
          value={rangeHours ?? ''}
          onChange={(e) => setRangeHours(e.target.value ? Number(e.target.value) : null)}
          className={controlClass}
        >
          {RANGES.map((r) => (
            <option key={r.label} value={r.hours ?? ''}>
              {r.label}
            </option>
          ))}
        </select>
      </div>

      <TableShell footer={<Pager page={page} pageSize={PAGE_SIZE} total={data.total} onChange={setPage} />}>
        <thead className="border-b border-slate-100 dark:border-slate-800">
          <tr>
            <Th>Time</Th>
            <Th>Event</Th>
            <Th>Outcome</Th>
            <Th>User</Th>
            <Th>Tenant</Th>
            <Th>Application</Th>
            <Th>Provider</Th>
            <Th>Detail</Th>
            <Th>IP</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {data.items.length === 0 ? (
            <EmptyRow columns={9} loading={loading} />
          ) : (
            data.items.map((e) => (
              <tr key={e.id} className="hover:bg-slate-50/60 dark:hover:bg-slate-800/30">
                <Td className="whitespace-nowrap">{formatDateTime(e.occurred_at)}</Td>
                <Td className="whitespace-nowrap">{EVENT_LABELS[e.event_type] ?? e.event_type}</Td>
                <Td>
                  <StatusPill status={e.outcome === 'success' ? 'success' : 'error'} label={e.outcome} />
                </Td>
                <Td className="font-semibold">{e.email ?? '—'}</Td>
                <Td>
                  <TenantCell slug={e.tenant} tenants={tenants} />
                </Td>
                <Td className="font-mono text-xs">{e.application ?? '—'}</Td>
                <Td>
                  <ProviderCell provider={e.provider} />
                </Td>
                <Td className="max-w-xs truncate" >
                  <span title={e.reason ?? ''}>{e.reason ?? '—'}</span>
                </Td>
                <Td className="font-mono text-xs">{e.ip_address ?? '—'}</Td>
              </tr>
            ))
          )}
        </tbody>
      </TableShell>
    </div>
  );
};
